import { NZAirports } from '../types/Locations';
import { HourlyForecastResponse, ErrorInfo } from '../models/HourlyForecastTimelineData';
import { apiClient } from './apiClient';

/**
 * Interface for batch forecast result with failure tracking
 */
export interface BatchHourlyFogForecastResult {
  forecasts: HourlyForecastResponse[];
  requestedCount: number;
  successCount: number;
  failedCount: number;
  hasPartialFailure: boolean;
}

/**
 * Service for fetching hourly fog forecasts from the API
 * Now uses centralized Axios client for standardized HTTP communication
 */
class HourlyFogForecastService {
  constructor() {
    // API base URL is now handled by the centralized apiClient
    console.log('HourlyFogForecastService initialized with centralized API client');
  }

  /**
   * Fetches an hourly fog forecast for a specific location
   * @param location The location ID from NZAirports enum
   * @param customNow Optional custom date to use instead of current time
   * @returns A promise resolving to the hourly fog forecast response
   */
  public async getHourlyFogForecast(location: NZAirports, customNow?: Date | null): Promise<HourlyForecastResponse> {
    try {
      // Log the input date for debugging
      if (customNow) {
        console.log(`HourlyFogForecastService received customNow: ${customNow.toISOString()}`);
      }

      // Get requested time - use custom date if provided, otherwise use current time
      const requestedTime = customNow ? customNow.toISOString() : new Date().toISOString();

      console.log(`Fetching hourly fog forecast for location ${location} with requested_time: ${requestedTime}`);

      // Make the API request using centralized Axios client
      const data: HourlyForecastResponse = await apiClient.get(
        `/api/fog-forecast-hourly/`,
        {
          params: {
            location: location,
            requested_time: requestedTime
          }
        }
      );

      console.log(`Received hourly forecast with ${data.hourly_buckets.length} prediction buckets and ${data.aggregated_actuals.length} actual buckets`);
      console.log(`Backend used requested_time: ${data.requested_time}`);

      // Log errors if present
      if (data.errors && data.errors.length > 0) {
        console.log(`Hourly forecast response contains ${data.errors.length} errors:`, data.errors);
        // Log the complete response for debugging when errors are present
        console.log('Complete hourly forecast response (with errors):', data);
      }

      return data;
    } catch (error) {
      console.error('Error fetching hourly fog forecast:', error);
      throw error;
    }
  }

  /**
   * Split an array into balanced chunks with smaller chunks first
   * Example: 21 items with chunkSize 5 → [4,4,4,4,5] instead of [5,5,5,5,1]
   */
  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    if (array.length === 0) return [];

    const numChunks = Math.ceil(array.length / chunkSize);
    const baseSize = Math.floor(array.length / numChunks);
    const remainder = array.length % numChunks;

    const chunks: T[][] = [];
    let currentIndex = 0;

    for (let i = 0; i < numChunks; i++) {
      const currentChunkSize = i < (numChunks - remainder) ? baseSize : baseSize + 1;
      chunks.push(array.slice(currentIndex, currentIndex + currentChunkSize));
      currentIndex += currentChunkSize;
    }

    return chunks;
  }

  /**
   * Fetches hourly fog forecasts for multiple locations using parallel chunked requests
   * @param locations Array of location IDs from NZAirports enum
   * @param customNow Optional custom date to use instead of current time
   * @param onChunkReceived Optional callback invoked as each chunk completes (for progressive UI updates)
   * @param chunkSize Number of locations to fetch per chunk (default: 5)
   * @returns A promise resolving to batch forecast result with failure tracking
   */
  public async getBatchHourlyFogForecast(
    locations: NZAirports[],
    customNow?: Date | null,
    onChunkReceived?: (chunk: HourlyForecastResponse[]) => void,
    chunkSize: number = 5
  ): Promise<BatchHourlyFogForecastResult> {
    try {
      // Get requested time - use custom date if provided, otherwise use current time
      const requestedTime = customNow ? customNow.toISOString() : new Date().toISOString();

      console.log(`Fetching batch hourly fog forecast for ${locations.length} locations with requested_time: ${requestedTime} (chunk size: ${chunkSize})`);

      // Split locations into chunks
      const chunks = this.chunkArray(locations, chunkSize);
      console.log(`Split into ${chunks.length} chunks for parallel fetching`);

      // Fetch all chunks in parallel using Promise.allSettled for partial failure handling
      const allForecasts: HourlyForecastResponse[] = [];
      let failedChunks = 0;

      const results = await Promise.allSettled(
        chunks.map(async (chunk, chunkIndex) => {
          // Build query params for this chunk
          const params = new URLSearchParams();
          chunk.forEach(loc => params.append('locations', loc.toString()));
          params.append('requested_time', requestedTime);

          // Make the API request for this chunk
          const response: { forecasts: HourlyForecastResponse[] } = await apiClient.get(
            `/api/fog-forecast-hourly/batch?${params.toString()}`
          );

          console.log(`Chunk ${chunkIndex + 1}/${chunks.length} received: ${response.forecasts.length} hourly forecasts`);

          // Add to results
          allForecasts.push(...response.forecasts);

          // Call callback if provided (for progressive UI updates)
          if (onChunkReceived) {
            try {
              onChunkReceived(response.forecasts);
            } catch (callbackError) {
              console.error(`Error in onChunkReceived callback for chunk ${chunkIndex + 1}:`, callbackError);
            }
          }

          return response.forecasts.length;
        })
      );

      // Log any failed chunks
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          failedChunks++;
          console.error(`Chunk ${index + 1}/${chunks.length} failed:`, result.reason);
        }
      });

      if (failedChunks > 0) {
        console.warn(`⚠️ ${failedChunks}/${chunks.length} chunks failed. Returned ${allForecasts.length} hourly forecasts from successful chunks.`);
      } else {
        console.log(`✅ All chunks received: ${allForecasts.length} total hourly forecasts`);
      }

      return {
        forecasts: allForecasts,
        requestedCount: locations.length,
        successCount: allForecasts.length,
        failedCount: locations.length - allForecasts.length,
        hasPartialFailure: failedChunks > 0
      };
    } catch (error) {
      console.error('Error fetching batch hourly fog forecast:', error);
      throw error;
    }
  }
}

// Create a singleton instance
const hourlyFogForecastService = new HourlyFogForecastService();

// Export the service instance and types
export { hourlyFogForecastService };
export type { HourlyForecastResponse, ErrorInfo };
